// Illustrations vectorielles des morphologies.
// Silhouette féminine en trait fin + forme géométrique représentant la morpho.
// Palette thémable : 'light' (doré sur clair) ou 'dark' (rose néon sur sombre).

import type { Morphology } from '../engine/types';

type Theme = 'light' | 'dark';

interface Palette {
  shapeFill: string;
  shapeStroke: string;
  line: string;
}

const PALETTES: Record<Theme, Palette> = {
  light: { shapeFill: 'rgba(232,132,44,0.40)', shapeStroke: '#e8842c', line: '#b9a89a' },
  dark: { shapeFill: 'rgba(232,132,44,0.32)', shapeStroke: '#e8842c', line: '#cfcfd6' },
};

/** Contour du corps (tête + chignon + buste + jambes). */
function body(p: Palette): string {
  return `
  <g fill="none" stroke="${p.line}" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round">
    <circle cx="80" cy="20" r="6"/>
    <ellipse cx="80" cy="42" rx="13" ry="16"/>
    <path d="M67 52 C60 56 56 62 54 70 L48 104 M93 52 C100 56 104 62 106 70 L112 104"/>
    <path d="M64 60 C62 80 60 96 66 112 C60 134 58 156 62 178 L72 250 M96 60 C98 80 100 96 94 112 C100 134 102 156 98 178 L88 250"/>
    <path d="M66 112 C74 118 86 118 94 112"/>
    <path d="M72 250 L70 320 L66 360 M88 250 L90 320 L94 360"/>
    <path d="M62 178 C74 186 86 186 98 178"/>
  </g>`;
}

/** Formes par morphologie (utilise la palette pour fill/stroke). */
function overlays(p: Palette): Record<Morphology | 'unknown', string> {
  const f = `fill="${p.shapeFill}" stroke="${p.shapeStroke}" stroke-width="1.6"`;
  return {
    A: `<path d="M80 70 L102 184 L58 184 Z" ${f}/>`,
    V: `<path d="M56 66 L104 66 L80 176 Z" ${f}/>`,
    H: `<rect x="60" y="66" width="40" height="118" rx="3" ${f}/>`,
    X: `<path d="M58 66 L102 66 L84 120 L102 184 L58 184 L76 120 Z" ${f}/>`,
    '8': `<path d="M54 64 C44 64 44 104 78 118 C44 132 44 188 80 188 C116 188 116 132 82 118 C116 104 116 64 106 64 C96 64 92 96 80 110 C68 96 64 64 54 64 Z" ${f}/>`,
    O: `<ellipse cx="80" cy="120" rx="28" ry="60" ${f}/>`,
    unknown: `<text x="80" y="135" text-anchor="middle" font-size="64" font-weight="700" ${f}>?</text>`,
  };
}

/** Retourne le SVG (string) d'une silhouette. */
export function silhouetteSVG(morpho: Morphology | 'unknown', theme: Theme = 'light'): string {
  const p = PALETTES[theme];
  const ov = overlays(p);
  const overlay = ov[morpho] ?? ov.unknown;
  return `<svg viewBox="0 0 160 370" xmlns="http://www.w3.org/2000/svg" class="eteka-silhouette" role="img" aria-label="Morphologie ${morpho}">
    ${overlay}
    ${body(p)}
  </svg>`;
}

export const SILHOUETTE_TITLES: Record<Morphology | 'unknown', string> = {
  A: 'Triangle',
  V: 'Triangle inversé',
  H: 'Rectangle',
  X: 'Sablier',
  '8': 'Sablier généreux',
  O: 'Ovale / Ronde',
  unknown: 'Je ne sais pas',
};

export const SILHOUETTE_DESCRIPTIONS: Record<Morphology | 'unknown', string> = {
  A: 'Tes hanches sont plus larges que tes épaules',
  V: 'Tes épaules sont plus larges que tes hanches',
  H: 'Épaules, taille et hanches aux mêmes proportions',
  X: 'Épaules ≈ hanches, taille bien marquée',
  '8': 'Buste et hanches généreux, taille très fine',
  O: 'Corps plus rond, plus large au niveau du ventre',
  unknown: 'Aide-moi à le découvrir avec quelques questions',
};
